/**
 * SOLID Review
 * S — Strong. Sole job is fetching and caching a LiveKit access token keyed by room/player, with expiry
 *     checking. getToken/invalidate/invalidateAll/fetchAndCache/isValid/buildCacheKey are all in service
 *     of that one concern.
 * O — Acceptable. The GET_LIVEKIT_TOKEN query and cache-validity margin (30s) are fixed inline; extending
 *     to a different token source would mean editing this class rather than swapping a dependency, but for
 *     a single-purpose token fetcher that's a reasonable, low-risk coupling.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. A small, cohesive public surface (getToken/invalidate/invalidateAll) — no caller is forced
 *     to depend on unrelated methods.
 * D — Strong (was Acceptable). Takes the NARROW executor from graphQLMediaDomainPort, not ApolloClient:
 *     singleton, which is good for testability, but it is not built behind an application-layer
 *     port/interface of its own — it's a standalone concrete class that nothing abstracts over yet. Noted
 *     plainly, not automatically a defect.
 */
import { gql } from '@apollo/client';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { Result } from '../../shared/Result';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import { ok, fail } from '../../shared/Result';

// ---------------------------------------------------------------------------
// GraphQL Operation
// ---------------------------------------------------------------------------

const GET_LIVEKIT_TOKEN = gql`
  query GetLiveKitToken($roomId: ID!, $playerId: ID!) {
    getLiveKitToken(roomId: $roomId, playerId: $playerId) {
      token
      serverUrl
      expiresAt
    }
  }
`;

// ---------------------------------------------------------------------------
// Response shape returned by the query
// ---------------------------------------------------------------------------

export interface IPayloadLiveKitToken {
  readonly token: string;
  readonly serverUrl: string;
  readonly expiresAt: number;
}

interface IGetLiveKitTokenQueryResult {
  getLiveKitToken: IPayloadLiveKitToken;
}


// ---------------------------------------------------------------------------
// Cache entry
// ---------------------------------------------------------------------------

interface ICacheEntry {
  readonly payload: IPayloadLiveKitToken;
  readonly fetchedAt: number;
}

// ---------------------------------------------------------------------------
// LiveKitTokenService
// ---------------------------------------------------------------------------

/**
 * Fetches a LiveKit access token from the backend GraphQL endpoint and caches
 * the result for the lifetime of the session (or until the token expires).
 * Cached entries are keyed by `${roomId}:${playerId}` so that each
 * participant/room combination is stored independently.
 */
export class LiveKitTokenService {
  private readonly cache = new Map<string, ICacheEntry>();

  constructor(
    /**
     * The media domain's executor, from ComponentFrontendInfrastructure's socket (2026-08-30).
     *
     * This was the raw ApolloClient singleton, handed over in adapterRegistry ONE LINE BELOW the
     * sibling ApolloLiveKitAdapter that was already correctly fed from graphQLMediaDomainPort. Same
     * domain, two different paths, one line apart. It only ever called `.query`.
     */
    private readonly apolloClient: IPortGraphQLDomainClient,
  ) {}

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Returns a valid LiveKit token for the given room/player combination.
   * If a non-expired token already exists in the local cache it is returned
   * directly without a network round-trip.
   */
  async getToken(
    roomId: RoomId,
    playerId: PlayerId,
  ): Promise<Result<IPayloadLiveKitToken>> {
    const cacheKey = this.buildCacheKey(roomId, playerId);

    const cached = this.cache.get(cacheKey);
    if (cached !== undefined && this.isValid(cached)) {
      return ok(cached.payload);
    }

    return this.fetchAndCache(roomId, playerId, cacheKey);
  }

  /**
   * Explicitly removes the cached token for the given room/player combination.
   * Should be called when the player leaves the LiveKit room so that a fresh
   * token will be fetched on the next join.
   */
  invalidate(roomId: RoomId, playerId: PlayerId): void {
    const cacheKey = this.buildCacheKey(roomId, playerId);
    this.cache.delete(cacheKey);
  }

  /**
   * Clears all cached tokens. Useful when the local session is destroyed.
   */
  invalidateAll(): void {
    this.cache.clear();
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  private async fetchAndCache(
    roomId: RoomId,
    playerId: PlayerId,
    cacheKey: string,
  ): Promise<Result<IPayloadLiveKitToken>> {
    try {
      const { data, error } = await this.apolloClient.query<
        IGetLiveKitTokenQueryResult
      >({
        query: GET_LIVEKIT_TOKEN,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        variables: { roomId, playerId } as any,
        fetchPolicy: 'network-only',
      });

      if (error) {
        return fail(
          new Error(`LiveKit token query returned GraphQL error: ${error.message}`),
        );
      }

      if (data?.getLiveKitToken === undefined || data.getLiveKitToken === null) {
        return fail(
          new Error(
            `LiveKit token query returned no data for room=${roomId} player=${playerId}`,
          ),
        );
      }

      const payload: IPayloadLiveKitToken = {
        token: data.getLiveKitToken.token,
        serverUrl: data.getLiveKitToken.serverUrl,
        expiresAt: data.getLiveKitToken.expiresAt,
      };

      this.cache.set(cacheKey, {
        payload,
        fetchedAt: Date.now(),
      });

      return ok(payload);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unknown error fetching LiveKit token';
      return fail(new Error(`LiveKitTokenService.fetchAndCache failed: ${message}`));
    }
  }

  /**
   * A cached entry is considered valid if the token's expiry timestamp
   * (supplied in seconds since epoch by the backend) is more than 30 seconds
   * in the future, giving a small safety margin for clock drift and connection
   * setup time.
   */
  private isValid(entry: ICacheEntry): boolean {
    const nowSeconds = Math.floor(Date.now() / 1_000);
    const safetyMarginSeconds = 30;
    return entry.payload.expiresAt > nowSeconds + safetyMarginSeconds;
  }

  private buildCacheKey(roomId: RoomId, playerId: PlayerId): string {
    return `${roomId}:${playerId}`;
  }
}